import { atom } from 'jotai';
import type { createStore } from 'jotai/vanilla';
import type {
	AiCostBreakdown,
	ServiceAvailability,
	VendorFaqQuestion,
	VendorFaqSeasonal,
} from 'types/vendor/properties';
import type { EstimateServiceBreakdown } from '../../components/pricing/SeasonalPricing/utils/estimateDisplay';
import type { EstimateDisplay } from '../../components/pricing/types';
import { isLiteStorefrontAtom } from './shared';
import { hasCouplesUsuallySpendAtom } from './shared-pricing';

export type CostBreakdown = {
	peak: {
		reception: string | null;
		ceremony: string | null;
		barServices: string | null;
	};
	offPeak: {
		reception: string | null;
		ceremony: string | null;
		barServices: string | null;
	};
};

export type PricingDetails = {
	reception: string | null;
	ceremony: string | null;
	barService: string | null;
	peakMonths: string | null;
	offPeakMonths: string | null;
};

type PricingState = {
	startingCost: string | null;
	peakCUS: string | null;
	offPeakCUS: string | null;
	offPeakSaving: string | null;
	couplesUsuallySpend: string | null;
	guestCapacity: string | null;
	costBreakdown: CostBreakdown;
	pricingDetails: PricingDetails;
	pdfUrl: string | null;
	venueEstimateUsuallySpend: string | null | undefined;
	rawAiCostBreakdown: AiCostBreakdown | null;
};

/** @internal */
export const initialState: PricingState = {
	startingCost: null,
	peakCUS: null,
	offPeakCUS: null,
	offPeakSaving: null,
	couplesUsuallySpend: null,
	guestCapacity: null,
	costBreakdown: {
		peak: {
			reception: null,
			ceremony: null,
			barServices: null,
		},
		offPeak: {
			reception: null,
			ceremony: null,
			barServices: null,
		},
	},
	pricingDetails: {
		reception: null,
		ceremony: null,
		barService: null,
		peakMonths: null,
		offPeakMonths: null,
	},
	pdfUrl: null,
	venueEstimateUsuallySpend: '',
	rawAiCostBreakdown: null,
};

export const startingCostAtom = atom(initialState.startingCost);
export const guestCapacityAtom = atom(initialState.guestCapacity);
export const formattedGuestCapacityAtom = atom((get) => {
	const guestCapacity = get(guestCapacityAtom);
	if (!guestCapacity) return null;
	if (guestCapacity.includes('-')) {
		return `${guestCapacity.replace('-', ' to ')} guests`;
	}
	if (guestCapacity.includes('+')) {
		return `${guestCapacity} guests`;
	}
	return null;
});
export const guestCapacityMaxAtom = atom((get) => {
	const guestCapacity = get(guestCapacityAtom);
	if (!guestCapacity) return null;
	if (!guestCapacity.includes('-')) return guestCapacity;
	return guestCapacity.split('-')[1];
});
export const pricingDetailsAtom = atom(initialState.pricingDetails);
export const formattedPricingDetailsAtom = atom((get) => {
	const pricingDetails = get(pricingDetailsAtom);

	return {
		reception: pricingDetails.reception || 'Contact for price',
		ceremony: pricingDetails.ceremony || 'Contact for price',
		barService: pricingDetails.barService
			? `${pricingDetails.barService} per person`
			: 'Contact for price',
	};
});
export const hasPricingDetailsAtom = atom((get) => {
	const { reception, ceremony, barService } = get(pricingDetailsAtom);
	return Boolean(reception || ceremony || barService);
});

export const pdfUrlAtom = atom(initialState.pdfUrl);

export const hidePricingAtom = atom((get) => {
	const hasCouplesUsuallySpend = get(hasCouplesUsuallySpendAtom);
	const isLiteStorefront = get(isLiteStorefrontAtom);

	return isLiteStorefront && !hasCouplesUsuallySpend;
});

export const peakCUSAtom = atom(initialState.peakCUS);
export const offPeakCUSAtom = atom(initialState.offPeakCUS);
export const offPeakSavingAtom = atom(initialState.offPeakSaving);
export const couplesUsuallySpendAtom = atom(initialState.couplesUsuallySpend);
export const venueEstimateUsuallySpendAtom = atom(
	initialState.venueEstimateUsuallySpend,
);
export const costBreakdownAtom = atom(initialState.costBreakdown);
export const estimateDisplayAtom = atom<EstimateDisplay | null>(null);
export const estimateServiceBreakdownAtom =
	atom<EstimateServiceBreakdown | null>(null);
export const rawSeasonalPricingAtom = atom<VendorFaqSeasonal | null>(null);
export const rawAiCostBreakdownAtom = atom<AiCostBreakdown | null>(null);
export type FreePricingService = 'reception' | 'ceremony';
export const freeServicesAtom = atom<FreePricingService[]>([]);
export const pricingEstimateOptOutAtom = atom(false);

const FREE_SERVICE_QUESTIONS: Record<FreePricingService, string> = {
	reception: 'Do you have a site fee for wedding receptions at your venue?',
	ceremony: 'Do you have a site fee for wedding ceremonies at your venue?',
};

export const getFreePricingServices = (
	questions: VendorFaqQuestion[] | null | undefined,
): FreePricingService[] =>
	(Object.entries(FREE_SERVICE_QUESTIONS) as [FreePricingService, string][])
		.filter(([, expectedQuestion]) =>
			questions?.some(
				({ question, answers }) =>
					question === expectedQuestion &&
					answers.some((answer) => answer.trim().toLowerCase() === 'no'),
			),
		)
		.map(([service]) => service);

const INITIAL_SERVICE_AVAILABILITY: ServiceAvailability = {
	ceremony: false,
	reception: true,
	barServices: false,
	catering: false,
};
export const serviceAvailabilityAtom = atom<ServiceAvailability>(
	INITIAL_SERVICE_AVAILABILITY,
);

const PRICING_SERVICES: (keyof ServiceAvailability)[] = [
	'ceremony',
	'reception',
	'catering',
	'barServices',
];

const isPositivePrice = (value: number | null | undefined) =>
	typeof value === 'number' && value > 0;

export const hasPaidServicePricing = ({
	services,
	seasonalPricing,
	aiCostBreakdown,
	freeServices,
	serviceAvailability,
}: {
	services: readonly (keyof ServiceAvailability)[];
	seasonalPricing: VendorFaqSeasonal | null;
	aiCostBreakdown: AiCostBreakdown | null;
	freeServices: readonly FreePricingService[];
	serviceAvailability: ServiceAvailability;
}) => {
	const freeServiceSet = new Set<string>(freeServices);

	return services.some((service) => {
		if (!serviceAvailability[service] || freeServiceSet.has(service))
			return false;

		const peakPrice = seasonalPricing?.peak?.services?.[service];
		const offPeakPrice = seasonalPricing?.offPeak?.services?.[service];
		const aiAmount = aiCostBreakdown?.[service]?.price.amount;

		return (
			isPositivePrice(peakPrice) ||
			isPositivePrice(offPeakPrice) ||
			isPositivePrice(aiAmount?.value) ||
			isPositivePrice(aiAmount?.min) ||
			isPositivePrice(aiAmount?.max)
		);
	});
};

export const shouldUsePersonalizedQuoteAtom = atom((get) => {
	if (get(pricingEstimateOptOutAtom)) return true;
	if (get(startingCostAtom)) return false;

	const seasonalPricing = get(rawSeasonalPricingAtom);
	const aiCostBreakdown = get(rawAiCostBreakdownAtom);
	const freeServices = get(freeServicesAtom);
	const serviceAvailability = get(serviceAvailabilityAtom);

	return !hasPaidServicePricing({
		services: PRICING_SERVICES,
		seasonalPricing,
		aiCostBreakdown,
		freeServices,
		serviceAvailability,
	});
});

export const pricingSnackbarAtom = atom<{
	message: string;
	conversationUrl?: string;
} | null>(null);

/** @knipignore experiment - please remove when used */
export const hasSeasonalPricingAtom = atom((get) => {
	const peakCUS = get(peakCUSAtom);
	const offPeakCUS = get(offPeakCUSAtom);
	const venueEstimateUsuallySpend = get(venueEstimateUsuallySpendAtom);
	const freeServices = get(freeServicesAtom);
	const costBreakdown = get(costBreakdownAtom);
	const hasCostBreakdownServices = Boolean(
		costBreakdown.peak.reception ||
			costBreakdown.peak.ceremony ||
			costBreakdown.peak.barServices ||
			costBreakdown.offPeak.reception ||
			costBreakdown.offPeak.ceremony ||
			costBreakdown.offPeak.barServices,
	);
	return Boolean(
		peakCUS ||
			offPeakCUS ||
			venueEstimateUsuallySpend ||
			hasCostBreakdownServices ||
			freeServices.length > 0,
	);
});

export const getPricingDataToHydrate = (
	store: ReturnType<typeof createStore>,
) => {
	const startingCost = store.get(startingCostAtom);
	const guestCapacity = store.get(guestCapacityAtom);
	const pricingDetails = store.get(pricingDetailsAtom);
	const pdfUrl = store.get(pdfUrlAtom);
	const peakCUS = store.get(peakCUSAtom);
	const offPeakCUS = store.get(offPeakCUSAtom);
	const offPeakSaving = store.get(offPeakSavingAtom);
	const couplesUsuallySpend = store.get(couplesUsuallySpendAtom);
	const venueEstimateUsuallySpend = store.get(venueEstimateUsuallySpendAtom);
	const costBreakdown = store.get(costBreakdownAtom);
	const rawSeasonalPricing = store.get(rawSeasonalPricingAtom);
	const rawAiCostBreakdown = store.get(rawAiCostBreakdownAtom);
	const freeServices = store.get(freeServicesAtom);
	const pricingEstimateOptOut = store.get(pricingEstimateOptOutAtom);
	const serviceAvailability = store.get(serviceAvailabilityAtom);

	return {
		startingCost,
		guestCapacity,
		pricingDetails,
		pdfUrl,
		peakCUS,
		offPeakCUS,
		offPeakSaving,
		couplesUsuallySpend,
		venueEstimateUsuallySpend,
		costBreakdown,
		rawSeasonalPricing,
		rawAiCostBreakdown,
		freeServices,
		pricingEstimateOptOut,
		serviceAvailability,
	};
};

export const getPricingInitialValues = (
	values: ReturnType<typeof getPricingDataToHydrate>,
) => {
	const {
		startingCost,
		guestCapacity,
		pricingDetails,
		pdfUrl,
		peakCUS,
		offPeakCUS,
		offPeakSaving,
		couplesUsuallySpend,
		venueEstimateUsuallySpend,
		costBreakdown,
		rawSeasonalPricing,
		rawAiCostBreakdown,
		freeServices,
		pricingEstimateOptOut,
		serviceAvailability,
	} = values || {};
	return [
		[startingCostAtom, startingCost || initialState.startingCost],
		[guestCapacityAtom, guestCapacity || initialState.guestCapacity],
		[pricingDetailsAtom, pricingDetails || initialState.pricingDetails],
		[pdfUrlAtom, pdfUrl || initialState.pdfUrl],
		[offPeakSavingAtom, offPeakSaving || initialState.offPeakSaving],
		[peakCUSAtom, peakCUS || initialState.peakCUS],
		[offPeakCUSAtom, offPeakCUS || initialState.offPeakCUS],
		[
			couplesUsuallySpendAtom,
			couplesUsuallySpend || initialState.couplesUsuallySpend,
		],
		[
			venueEstimateUsuallySpendAtom,
			venueEstimateUsuallySpend || initialState.venueEstimateUsuallySpend,
		],
		[costBreakdownAtom, costBreakdown || initialState.costBreakdown],
		[rawSeasonalPricingAtom, rawSeasonalPricing ?? null],
		[rawAiCostBreakdownAtom, rawAiCostBreakdown ?? null],
		[freeServicesAtom, freeServices ?? []],
		[pricingEstimateOptOutAtom, pricingEstimateOptOut ?? false],
		[
			serviceAvailabilityAtom,
			serviceAvailability ?? INITIAL_SERVICE_AVAILABILITY,
		],
	];
};
